/* hydrus-shipping-protection.js
 * Companion to snippets/hydrus-shipping-protection-toggle.liquid.
 *
 * Reconciles the cart's shipping-protection state against the desired
 * state computed from:
 *   - cart.total_price excluding any current protection line
 *   - cart.attributes.hydrus_shipping_protection_opted_out ("true" | empty)
 *   - configured rate (bps) + min-cart threshold
 *   - 75-variant price ladder from the hydrus-shipping-protection product
 *
 * Desired state:
 *   - opted_out=true OR cart_total_excl_protection < min  → no protection variant
 *   - else                                                 → variant with smallest price >= ceil(total × rate)
 *
 * Reconcile loop fires:
 *   - On DOMContentLoaded (catches direct /cart nav)
 *   - On `cart:refresh` event (covers ATC from PDP, qty changes, remove, xsell add)
 *
 * Mutations use /cart/update.js with both `updates` (to swap variants atomically
 * via key:qty map) and `attributes` (to persist opt-out), so we only make one
 * round-trip per reconciliation and set the opt-out flag in the same request
 * when the customer toggles off.
 *
 * Singleton guard prevents double-binding across Section Rendering API innerHTML
 * swaps and multiple snippet renders on the same page (cart + drawer).
 */
(function () {
  if (window.__hydrusSPLoaded) return; window.__hydrusSPLoaded = true;
  if (window.HydrusShippingProtection) return;
  window.HydrusShippingProtection = true;

  const LOG_PREFIX = '[HydrusShippingProtection]';
  // In-flight guard to prevent reconcile loops during our own cart mutations
  let reconciling = false;

  function root() {
    // Prefer drawer's copy if open, else cart-page copy. Either is fine for reconciliation.
    return document.querySelector('[data-hydrus-sp-root]');
  }

  function readConfig(el) {
    if (!el) return null;
    try {
      return {
        rateBps: Number(el.getAttribute('data-rate-bps')) || 150,
        minCartCents: Number(el.getAttribute('data-min-cart-cents')) || 2500,
        currentKey: el.getAttribute('data-current-key') || '',
        currentPriceCents: Number(el.getAttribute('data-current-price-cents')) || 0,
        optedOut: el.getAttribute('data-opted-out') === 'true',
        variants: JSON.parse(el.getAttribute('data-variants') || '[]'),
      };
    } catch (err) {
      console.error(LOG_PREFIX, 'readConfig parse failed', err);
      return null;
    }
  }

  function pickVariant(feeCents, variants) {
    // variants: [{id, priceCents}, ...] sorted or unsorted — we sort defensively
    const sorted = variants.slice().sort((a, b) => a.priceCents - b.priceCents);
    for (const v of sorted) {
      if (v.priceCents >= feeCents) return v;
    }
    return sorted[sorted.length - 1] || null;
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('cart fetch failed: ' + res.status);
    return res.json();
  }

  function getRoleMetafield(item) {
    // Shopify's cart.js doesn't return metafields per-line. We identify the
    // protection line by product handle — the handle is stable.
    return item && item.handle === 'shipping-protection'
      ? 'shipping_protection'
      : '';
  }

  async function reconcile(source) {
    if (reconciling) return;
    const el = root();
    if (!el) return;
    const cfg = readConfig(el);
    if (!cfg || !cfg.variants.length) return;

    reconciling = true;
    try {
      const cart = await fetchCart();
      // Find any protection line (by handle)
      const protectionLine = (cart.items || []).find(
        (it) => getRoleMetafield(it) === 'shipping_protection'
      );
      const cartTotalExclProtection =
        (cart.total_price || 0) - (protectionLine ? protectionLine.line_price : 0);

      // Opt-out flag
      const attrs = cart.attributes || {};
      const optedOut = attrs.hydrus_shipping_protection_opted_out === 'true';

      // Desired variant
      let desiredVariantId = null;
      if (!optedOut && cartTotalExclProtection >= cfg.minCartCents) {
        const feeCents = Math.ceil((cartTotalExclProtection * cfg.rateBps) / 10000);
        const picked = pickVariant(feeCents, cfg.variants);
        if (picked) desiredVariantId = picked.id;
      }

      const currentVariantId = protectionLine ? protectionLine.variant_id : null;

      // Compare numeric IDs loosely (JS numbers can differ from string IDs)
      const same =
        (desiredVariantId == null && currentVariantId == null) ||
        (desiredVariantId != null &&
          currentVariantId != null &&
          Number(desiredVariantId) === Number(currentVariantId));

      if (same) {
        return; // nothing to do
      }

      // Mutate: always first remove the current protection line if present,
      // then add the new one. Use /cart/update.js updates map + /cart/add.js
      // for the add (update.js can't add line items, only adjust existing).
      if (protectionLine && currentVariantId !== desiredVariantId) {
        const updates = {};
        updates[protectionLine.key] = 0;
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates }),
        });
      }
      if (desiredVariantId) {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: Number(desiredVariantId), quantity: 1 }],
          }),
        });
      }
      // Notify other cart surfaces to refresh. Dispatch AFTER mutations finish.
      document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { source: 'shipping-protection:' + (source || 'reconcile') } }));
    } catch (err) {
      console.error(LOG_PREFIX, 'reconcile failed', err);
    } finally {
      // Small cooldown so cart:refresh swaps finish before next reconcile
      setTimeout(() => {
        reconciling = false;
      }, 150);
    }
  }

  async function setOptOut(nextOptedOut) {
    // Persist opt-out flag. Remove protection line if opting out; add if opting back in.
    reconciling = true;
    try {
      const attrs = {};
      attrs.hydrus_shipping_protection_opted_out = nextOptedOut ? 'true' : '';
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
      });
    } catch (err) {
      console.error(LOG_PREFIX, 'setOptOut failed', err);
    } finally {
      reconciling = false;
    }
    // Trigger reconcile explicitly so the variant add/remove runs against the new attribute
    reconcile('toggle');
  }

  // Event delegation for toggle click (survives innerHTML swaps)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-hydrus-sp-toggle]');
    if (!btn) return;
    e.preventDefault();
    const pressed = btn.getAttribute('aria-checked') === 'true';
    const nextOptedOut = pressed; // currently ON → will opt out
    // Optimistic UI — flip aria-checked and class immediately
    btn.setAttribute('aria-checked', nextOptedOut ? 'false' : 'true');
    const rootEl = btn.closest('[data-hydrus-sp-root]');
    if (rootEl) {
      rootEl.classList.toggle('hydrus-sp-off', nextOptedOut);
      rootEl.classList.toggle('hydrus-sp-on', !nextOptedOut);
      rootEl.setAttribute('data-opted-out', nextOptedOut ? 'true' : 'false');
    }
    setOptOut(nextOptedOut);
  });

  // Reconcile on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => reconcile('load'));
  } else {
    reconcile('load');
  }

  // Reconcile on cart:refresh — but only if this event didn't originate from our own mutations
  document.addEventListener('cart:refresh', (e) => {
    const origin = e.detail && e.detail.source;
    if (origin && origin.indexOf('shipping-protection') === 0) return;
    reconcile('cart:refresh');
    setTimeout(() => reconcile('cart:refresh+600'), 600);
    setTimeout(() => reconcile('cart:refresh+1800'), 1800);
  });
  document.addEventListener('cart-drawer:open', () => setTimeout(() => reconcile('drawer-open'), 300));
})();
